// Drop-down options of assessment tasks (US 9398): what each option counts as, and the colours and icons a user
// can pick for it. Keep the colours and icons in step with web/app/Maintenance/Assessment/DropdownOptions.php.
export const OPTION_COLORS = {
  green: '#7FE0C4',
  emerald: '#0F7B5C',
  teal: '#1F7A72',
  mint: '#14A38B',
  cyan: '#1F8FB0',
  blue: '#3B6FD8',
  indigo: '#4F5BD5',
  violet: '#7B5CD6',
  plum: '#B5487A',
  pink: '#D2557A',
  red: '#E5484D',
  orange: '#E8722A',
  amber: '#C27C00',
  yellow: '#B8901F',
  olive: '#6A7A12',
  brown: '#A0522D',
  slate: '#5B6573',
  black: '#081F26'
};

// Light colours get a darker shade for their text and icon, so they stay readable on their pale background
const OPTION_TEXT_COLORS = {
  green: '#1F7A6A',
  yellow: '#8A6A12'
};

export const OPTION_ICONS = [
  'check-circle', 'check', 'times-circle', 'times', 'exclamation-triangle', 'exclamation-circle', 'info-circle', 'question',
  'ban', 'minus-circle', 'minus', 'adjust', 'hourglass-half', 'clock', 'thumbs-up', 'thumbs-down',
  'star', 'flag', 'shield-alt', 'lock', 'eye', 'eye-slash', 'arrow-up', 'arrow-down',
  'chart-line', 'bolt', 'bullseye', 'file-alt', 'file-exclamation', 'file-signature', 'redo', 'circle'
];

// What an option counts as, in the groups the task dialogs have always used
export const OPTION_VALUE_GROUPS = [
  { label: 'maintenance.questions.answer_type.positive_negative_label', values: ['Neutral', 'Positive', 'Negative'] },
  { label: 'maintenance.questions.answer_type.impact_assessment_label', values: ['Very low', 'Low', 'Medium', 'High', 'Very high'] }
];

// Options saved before colours and icons existed look like what they count as. Keep in step with
// DropdownOptions::STYLES, which the migration that gave old options their colour and icon used.
const VALUE_STYLES = {
  'Positive': { color: 'green', icon: 'check-circle' },
  'Neutral': { color: 'slate', icon: 'minus-circle' },
  'Negative': { color: 'red', icon: 'times-circle' },
  'Very low': { color: 'emerald', icon: 'arrow-down' },
  'Low': { color: 'olive', icon: 'arrow-down' },
  'Medium': { color: 'amber', icon: 'minus' },
  'High': { color: 'orange', icon: 'arrow-up' },
  'Very high': { color: 'red', icon: 'arrow-up' }
};

export function optionValue(option) {
  return option && option.value ? (option.value.value || option.value) : 'Neutral';
}

export function optionStyle(option) {
  let fallback = VALUE_STYLES[optionValue(option)] || VALUE_STYLES['Neutral'];
  let color = option && OPTION_COLORS[option.color] ? option.color : fallback.color;

  return {
    color: color,
    hex: OPTION_COLORS[color],
    text: OPTION_TEXT_COLORS[color] || null,
    icon: option && OPTION_ICONS.indexOf(option.icon) !== -1 ? option.icon : fallback.icon
  };
}

export function makeOption(title, value, color, icon) {
  let style = VALUE_STYLES[value] || VALUE_STYLES['Neutral'];
  return { title: title, value: { name: value, value: value }, color: color || style.color, icon: icon || style.icon };
}

export function parseOptions(json) {
  if (!json) {
    return [];
  }
  try {
    let options = typeof json === 'string' ? JSON.parse(json) : json;
    return Array.isArray(options) ? options : [];
  } catch (e) {
    return [];
  }
}

// Ready-made lists to start from; their titles are translation labels, filled in when chosen
export const OPTION_PRESETS = {
  effective: [
    ['dropdown_options.presets.effective.effective', 'Positive', 'green', 'check-circle'],
    ['dropdown_options.presets.effective.exceptions', 'Neutral', 'amber', 'exclamation-triangle'],
    ['dropdown_options.presets.effective.not_applicable', 'Neutral', 'slate', 'ban'],
    ['dropdown_options.presets.effective.not_effective', 'Negative', 'red', 'times-circle']
  ],
  yes_no: [
    ['dropdown_options.presets.yes_no.yes', 'Positive', 'green', 'thumbs-up'],
    ['dropdown_options.presets.yes_no.no', 'Negative', 'red', 'thumbs-down'],
    ['dropdown_options.presets.yes_no.not_applicable', 'Neutral', 'slate', 'ban']
  ],
  impact: [
    ['dropdown_options.presets.impact.very_low', 'Very low', 'emerald', 'arrow-down'],
    ['dropdown_options.presets.impact.low', 'Low', 'olive', 'arrow-down'],
    ['dropdown_options.presets.impact.medium', 'Medium', 'amber', 'minus'],
    ['dropdown_options.presets.impact.high', 'High', 'orange', 'arrow-up'],
    ['dropdown_options.presets.impact.very_high', 'Very high', 'red', 'arrow-up']
  ],
  maturity: [
    ['dropdown_options.presets.maturity.initial', 'Negative', 'red', 'circle'],
    ['dropdown_options.presets.maturity.repeatable', 'Neutral', 'orange', 'redo'],
    ['dropdown_options.presets.maturity.defined', 'Neutral', 'amber', 'file-alt'],
    ['dropdown_options.presets.maturity.managed', 'Positive', 'teal', 'chart-line'],
    ['dropdown_options.presets.maturity.optimised', 'Positive', 'green', 'star']
  ],
  received: [
    ['dropdown_options.presets.received.sufficient', 'Positive', 'green', 'file-signature'],
    ['dropdown_options.presets.received.more_needed', 'Neutral', 'blue', 'hourglass-half'],
    ['dropdown_options.presets.received.not_received', 'Negative', 'red', 'file-exclamation']
  ]
};
